import { Injectable, inject } from '@angular/core';
import { Observable, catchError, defer, map, of, shareReplay } from 'rxjs';
import { Company, User } from '@core/models';
import { BranchService } from '@core/services/branch/branch.service';
import { CompanySearch } from './company-search';

/**
 * A multi-company user's own companies, anyone else every active company, filtered by name in the
 * browser. The active-company list is fetched once and reused, so typing in the search box costs
 * no request.
 */
@Injectable({ providedIn: 'root' })
export class LegacyCompanySearchService implements CompanySearch {
  private branchService = inject(BranchService);

  private activeCompanies$ = defer(() =>
    this.branchService.getActiveCompanies()
  ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

  search(user: User, q: string): Observable<Company[]> {
    const companies$ = user.isMultiUser
      ? of(user.companies.filter((c) => c.active))
      : this.activeCompanies$;
    const needle = q.trim().toLowerCase();
    return companies$.pipe(
      map((companies) =>
        companies.filter((c) => (c.name ?? '').toLowerCase().includes(needle))
      ),
      // ErrorInterceptor already reports the failure; an empty answer keeps the selector's search
      // stream alive, so the next search retries the fetch.
      catchError(() => of([]))
    );
  }
}
